// Digital Portrait: a real 3D phone using the approved mockup as its screen texture.
async function initializeDigital(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const stage = root.querySelector('.pp-stage');
  const status = root.querySelector('.pp-status');
  const announcement = root.querySelector('.pp-announcement');
  const shadow = root.querySelector('.pp-shadow');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = matchMedia('(pointer: coarse)');
  const compact = matchMedia('(max-width: 749px)');
  const life = new AbortController();
  const motionScale = 1.30;
  const configuration = { tilt: 3.84 * motionScale, inertia: .23, motion: true };
  const geometries = [], materials = [], textures = [], targets = [];
  let disposed = false, renderer, resizeObserver, visibilityObserver;
  let frame = 0, lastTime = 0, visible = true, pointerInside = false;
  let playedMobile = false, demoStart = null, hasFocus = false;
  let px = 0, py = 0, rx = 0, ry = 0, lift = 0;
  const loadTimer = setTimeout(() => {
    if (root.dataset.ready !== 'true') status.textContent = 'Die 3D-Ansicht wird noch geladen. Die Handy-Vorschau ist bereits sichtbar.';
  }, 9000);

  function dispose() {
    if (disposed) return;
    disposed = true;
    clearTimeout(loadTimer);
    if (frame) cancelAnimationFrame(frame);
    life.abort();
    resizeObserver?.disconnect(); visibilityObserver?.disconnect();
    geometries.forEach(item => item.dispose());
    materials.forEach(item => item.dispose());
    textures.forEach(item => item.dispose());
    targets.forEach(item => item.dispose());
    renderer?.dispose(); renderer?.domElement.remove();
    delete root.dataset.initialized; delete root.dataset.ready;
  }
  root.disposePreview = dispose;

  try {
    const THREE = await import('https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js');
    if (disposed || !root.isConnected) { dispose(); return; }
    const image = new Image(); image.crossOrigin = 'anonymous'; image.src = root.dataset.phoneSrc;
    await image.decode();
    if (disposed || !root.isConnected) { dispose(); return; }
    const artwork = new THREE.Texture(image);
    artwork.colorSpace = THREE.SRGBColorSpace;
    artwork.minFilter = THREE.LinearMipmapLinearFilter;
    artwork.magFilter = THREE.LinearFilter;
    artwork.generateMipmaps = true; artwork.needsUpdate = true; textures.push(artwork);

    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.domElement.setAttribute('aria-hidden', 'true'); stage.append(renderer.domElement);
    artwork.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());
    const scene = new THREE.Scene();
    const fov = 2 * Math.atan(36 / (2 * 105)) * 180 / Math.PI;
    const camera = new THREE.PerspectiveCamera(fov, 1, .01, 20);
    const distance = 2.1, yaw = THREE.MathUtils.degToRad(5.5), elevation = THREE.MathUtils.degToRad(2);
    camera.position.set(distance * Math.sin(yaw) * Math.cos(elevation), -.008 + distance * Math.sin(elevation), distance * Math.cos(yaw) * Math.cos(elevation));
    camera.lookAt(0, -.008, 0);
    const model = new THREE.Group(); model.name = 'Digital Portrait · interactive phone'; scene.add(model);
    const restingRoll = THREE.MathUtils.degToRad(-1.5);

    function roundedShape(width, height, radius) {
      const x = -width / 2, y = -height / 2, shape = new THREE.Shape();
      shape.moveTo(x + radius, y);
      shape.lineTo(x + width - radius, y); shape.quadraticCurveTo(x + width, y, x + width, y + radius);
      shape.lineTo(x + width, y + height - radius); shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      shape.lineTo(x + radius, y + height); shape.quadraticCurveTo(x, y + height, x, y + height - radius);
      shape.lineTo(x, y + radius); shape.quadraticCurveTo(x, y, x + radius, y);
      return shape;
    }
    function mesh(geometry, material, z = 0) {
      geometries.push(geometry);
      const object = new THREE.Mesh(geometry, material); object.position.z = z; model.add(object);
      return object;
    }
    // Soft studio reflections are confined to the slender silver chassis.
    const envWidth = 128, envHeight = 64, pixels = new Float32Array(envWidth * envHeight * 4);
    for (let y = 0; y < envHeight; y++) for (let x = 0; x < envWidth; x++) {
      const u = (x + .5) / envWidth, v = (y + .5) / envHeight;
      const light = .18 + 2.2 * Math.exp(-(((u - .28) / .07) ** 2 + ((v - .34) / .27) ** 2))
        + 1.6 * Math.exp(-(((u - .75) / .05) ** 2 + ((v - .48) / .32) ** 2));
      const i = (y * envWidth + x) * 4;
      pixels[i] = pixels[i + 1] = pixels[i + 2] = light; pixels[i + 3] = 1;
    }
    const environment = new THREE.DataTexture(pixels, envWidth, envHeight, THREE.RGBAFormat, THREE.FloatType);
    environment.mapping = THREE.EquirectangularReflectionMapping; environment.needsUpdate = true;
    const generator = new THREE.PMREMGenerator(renderer), target = generator.fromEquirectangular(environment);
    generator.dispose(); environment.dispose(); targets.push(target);
    const silver = new THREE.MeshPhysicalMaterial({ color: 0xbfc3c6, metalness: .95, roughness: .25, envMap: target.texture, envMapIntensity: 1.1 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x080b0e, metalness: .2, roughness: .28, envMap: target.texture, envMapIntensity: .3 });
    const screenMaterial = new THREE.MeshBasicMaterial({ map: artwork, toneMapped: false });
    materials.push(silver, dark, screenMaterial);
    const body = new THREE.ExtrudeGeometry(roundedShape(.226, .513, .037), {
      depth: .010, bevelEnabled: true, bevelSegments: 3, bevelSize: .0015, bevelThickness: .0015, curveSegments: 20, steps: 1
    });
    body.translate(0, 0, -.005); mesh(body, silver);
    mesh(new THREE.ShapeGeometry(roundedShape(.224, .511, .036), 24), dark, .0066);

    // Projective UV mapping reads the approved image directly. No re-generation,
    // re-cropping of the car, lossy conversion or baked studio background.
    // These are the four virtual, unrounded screen corners in the 1672×941 mockup.
    const screenCorners = [[693, 108], [1012, 89], [984, 853], [665, 829]];
    const equations = [];
    for (const [i, [s, t]] of [[0, [0, 0]], [1, [1, 0]], [2, [1, 1]], [3, [0, 1]]]) {
      const [x, y] = screenCorners[i];
      equations.push([s, t, 1, 0, 0, 0, -x * s, -x * t, x]);
      equations.push([0, 0, 0, s, t, 1, -y * s, -y * t, y]);
    }
    for (let column = 0; column < 8; column++) {
      let pivot = column;
      for (let row = column + 1; row < 8; row++) if (Math.abs(equations[row][column]) > Math.abs(equations[pivot][column])) pivot = row;
      [equations[column], equations[pivot]] = [equations[pivot], equations[column]];
      const divisor = equations[column][column];
      if (Math.abs(divisor) < 1e-10) throw new Error('Display-Perspektive ungültig');
      for (let i = column; i <= 8; i++) equations[column][i] /= divisor;
      for (let row = 0; row < 8; row++) if (row !== column) {
        const factor = equations[row][column];
        for (let i = column; i <= 8; i++) equations[row][i] -= factor * equations[column][i];
      }
    }
    const h = equations.map(row => row[8]);
    const screenWidth = .218, screenHeight = .504, screenRadius = .033;
    const screenGeometry = new THREE.PlaneGeometry(screenWidth, screenHeight, 48, 128);
    const positions = screenGeometry.attributes.position, uvs = screenGeometry.attributes.uv;
    for (let i = 0; i < positions.count; i++) {
      const y = positions.getY(i), cornerY = Math.max(0, Math.abs(y) - (screenHeight / 2 - screenRadius));
      const halfWidth = screenWidth / 2 - screenRadius + Math.sqrt(Math.max(0, screenRadius ** 2 - cornerY ** 2));
      const x = positions.getX(i) / (screenWidth / 2) * halfWidth;
      positions.setX(i, x);
      const s = x / screenWidth + .5, t = .5 - y / screenHeight, denominator = h[6] * s + h[7] * t + 1;
      const imageX = (h[0] * s + h[1] * t + h[2]) / denominator;
      const imageY = (h[3] * s + h[4] * t + h[5]) / denominator;
      uvs.setXY(i, imageX / 1672, 1 - imageY / 941);
    }
    screenGeometry.computeBoundingSphere(); mesh(screenGeometry, screenMaterial, .0069);
    for (const [x, y, length] of [[.114, .083, .044], [-.114, .111, .028], [-.114, .070, .028]]) {
      const button = mesh(new THREE.BoxGeometry(.0025, length, .005), silver); button.position.set(x, y, 0);
    }
    scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.05); key.position.set(-.75, 1.05, 1.15); scene.add(key);
    const fill = new THREE.DirectionalLight(0xfffbf5, .55); fill.position.set(.85, .45, .85); scene.add(fill);

    const requestFrame = () => { if (!disposed && visible && !document.hidden && !frame) frame = requestAnimationFrame(tick); };
    const motionAllowed = () => configuration.motion && !reduced.matches;
    function controlsChanged() {
      if (!motionAllowed()) { px = py = rx = ry = lift = 0; demoStart = null; pointerInside = false; }
      requestFrame();
    }
    function resize() {
      if (disposed) return;
      const w = Math.ceil(renderer.domElement.getBoundingClientRect().width);
      if (w < 1) return;
      renderer.setPixelRatio(Math.min(Math.max(devicePixelRatio || 1, 2), 3, 2048 / w));
      renderer.setSize(w, w, false); camera.aspect = 1; camera.updateProjectionMatrix(); requestFrame();
    }
    function runDemo() {
      if (!motionAllowed() || disposed) return;
      demoStart = performance.now(); pointerInside = false; root.dataset.demo = 'playing'; requestFrame();
    }
    function tick(time) {
      frame = 0;
      if (disposed || !visible || document.hidden) return;
      const dt = lastTime ? Math.min((time - lastTime) / 1000, .05) : 1 / 60; lastTime = time;
      let targetX = 0, targetY = 0, targetLift = 0;
      const max = THREE.MathUtils.degToRad(configuration.tilt);
      if (motionAllowed()) {
        if (demoStart !== null) {
          const t = Math.min(1, (time - demoStart) / 2500), envelope = Math.sin(Math.PI * t) ** 2;
          targetY = Math.sin(t * Math.PI * 2) * max * envelope;
          targetX = -Math.sin(t * Math.PI) * max * .38 * envelope;
          targetLift = .006 * motionScale * envelope;
          if (t === 1) { demoStart = null; root.dataset.demo = 'complete'; }
        } else if (pointerInside || hasFocus) {
          const mouseMax = pointerInside ? max * 1.44 * 1.20 * 1.80 : max;
          targetY = px * mouseMax; targetX = py * mouseMax * .62; targetLift = pointerInside ? .0054 * motionScale : 0;
        }
      }
      const blend = 1 - Math.exp(-dt / configuration.inertia);
      rx += (targetX - rx) * blend; ry += (targetY - ry) * blend; lift += (targetLift - lift) * blend;
      model.rotation.set(rx, ry, restingRoll, 'YXZ'); model.position.y = lift;
      shadow.style.transform = `scale(${(1 - lift * 4).toFixed(4)},${(1 + lift * 4).toFixed(4)})`;
      shadow.style.opacity = (1 - lift * 13).toFixed(3);
      renderer.render(scene, camera);
      root.dataset.pose = `${(rx * 180 / Math.PI).toFixed(2)},${(ry * 180 / Math.PI).toFixed(2)}`;
      const moving = demoStart !== null || Math.abs(rx - targetX) + Math.abs(ry - targetY) + Math.abs(lift - targetLift) > .00002;
      if (moving) requestFrame(); else lastTime = 0;
    }
    stage.addEventListener('pointermove', event => {
      if (event.pointerType === 'touch' || !motionAllowed()) return;
      const bounds = stage.getBoundingClientRect();
      px = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width - .5) * 2));
      py = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height - .5) * 2));
      pointerInside = true; demoStart = null; requestFrame();
    }, { signal: life.signal, passive: true });
    for (const event of ['pointerleave', 'pointercancel']) stage.addEventListener(event, () => {
      pointerInside = false; px = py = 0; requestFrame();
    }, { signal: life.signal });
    stage.addEventListener('focus', () => { hasFocus = true; }, { signal: life.signal });
    stage.addEventListener('blur', () => { hasFocus = false; px = py = 0; requestFrame(); }, { signal: life.signal });
    stage.addEventListener('keydown', event => {
      if (!motionAllowed()) return;
      const directions = { ArrowLeft: [-.7, 0], ArrowRight: [.7, 0], ArrowUp: [0, -.7], ArrowDown: [0, .7] };
      if (directions[event.key]) { event.preventDefault(); [px, py] = directions[event.key]; hasFocus = true; demoStart = null; requestFrame(); }
      if (event.key === 'Escape') { px = py = 0; demoStart = null; requestFrame(); }
    }, { signal: life.signal });
    reduced.addEventListener('change', controlsChanged, { signal: life.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && frame) { cancelAnimationFrame(frame); frame = 0; }
      else { lastTime = 0; requestFrame(); }
    }, { signal: life.signal });
    renderer.domElement.addEventListener('webglcontextlost', event => {
      event.preventDefault(); dispose(); status.textContent = 'Die 3D-Ansicht wurde unterbrochen. Die Handy-Vorschau bleibt sichtbar.';
    }, { signal: life.signal });
    resizeObserver = new ResizeObserver(resize); resizeObserver.observe(stage);
    visibilityObserver = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (!visible && frame) { cancelAnimationFrame(frame); frame = 0; }
      if (visible) {
        lastTime = 0;
        if ((coarse.matches || compact.matches) && !playedMobile && entry.intersectionRatio >= .4 && motionAllowed()) { playedMobile = true; runDemo(); }
        else requestFrame();
      }
    }, { threshold: [0, .4] });
    visibilityObserver.observe(stage);
    resize(); controlsChanged(); model.rotation.z = restingRoll; renderer.render(scene, camera);
    root.dataset.ready = 'true'; clearTimeout(loadTimer); status.textContent = '';
    announcement.textContent = 'Die interaktive 3D-Ansicht ist bereit.';
    window.addEventListener('pagehide', event => { if (!event.persisted) dispose(); }, { signal: life.signal });
    window.addEventListener('pageshow', () => { lastTime = 0; requestFrame(); }, { signal: life.signal });
  } catch (error) {
    dispose(); console.warn('Petrol Portraits Digital preview:', error);
    status.textContent = 'Die 3D-Ansicht konnte hier nicht gestartet werden. Die Handy-Vorschau bleibt sichtbar.';
  }
}

if (!customElements.get('pp-digital-preview')) {
  customElements.define('pp-digital-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.startObserver?.disconnect();
      this.startObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.startObserver.disconnect(); initializeDigital(this);
      }, { rootMargin: '400px' });
      this.startObserver.observe(this);
    }
    disconnectedCallback() { this.startObserver?.disconnect(); this.disposePreview?.(); }
  });
}
